import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, TextField, CircularProgress, Chip, Alert } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import Markdown from 'react-markdown';
import { proposalService } from '../services/api';

export default function StepSummary({ session, setSession, onNext, onBack }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(session.analysis || null);
  const [summary, setSummary] = useState(session.analysis?.summary || '');
  const [editText, setEditText] = useState(session.analysis?.summary || '');
  const [reSumLoading, setReSumLoading] = useState(false);

  useEffect(() => { if (!analysis) runAnalysis(); }, []); // eslint-disable-line

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const result = await proposalService.analyze(session.sessionId);
      setAnalysis(result);
      setSummary(result.summary);
      setEditText(result.summary);
      setSession(s => ({ ...s, analysis: result }));
    } catch (err) { alert('Analysis failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };

  const handleReSummarize = async () => {
    setReSumLoading(true);
    try {
      const result = await proposalService.reSummarize(session.sessionId, editText);
      setSummary(result.summary);
      setEditText(result.summary);
      setAnalysis(a => ({ ...a, summary: result.summary }));
      setSession(s => ({ ...s, analysis: { ...s.analysis, summary: result.summary } }));
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setReSumLoading(false); }
  };

  const handleReAnalyze = async () => {
    setAnalysis(null);
    setSummary('');
    setEditText('');
    setSession(s => ({ ...s, analysis: null }));
    runAnalysis();
  };

  if (loading) {
    return (
      <Paper sx={{ p: 8, border: '1px solid #E2E8F0', textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6">Analyzing your RFP/RFD documents...</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Identifying the ask • Extracting deliverables • Checking AWS relevance</Typography>
      </Paper>
    );
  }
  if (!analysis) return null;

  const ctx = analysis.config?.project_context || {};
  const reqs = analysis.requirements || {};
  const cloudMappings = analysis.cloud_services_mapped || reqs.cloud_services_mapped || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Context */}
      {ctx.name && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          {[['Project', ctx.name, '#6D28D9'], ['Domain', ctx.domain, '#DB2777'], ['Region', ctx.region, '#0891B2']].map(([label, val, color]) => (
            <Paper key={label} sx={{ p: 2.5, border: '1px solid #E2E8F0', borderTop: `3px solid ${color}`, flex: 1 }}>
              <Typography variant="overline" color="text.secondary" fontSize={10}>{label}</Typography>
              <Typography variant="h6" fontWeight={700}>{val || 'N/A'}</Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Cloud Service Mappings */}
      {cloudMappings.length > 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <strong>☁️ Cloud services mapped to AWS:</strong>{' '}
          {cloudMappings.map((m, i) => (
            <span key={i}>{m.original} → {m.aws_equivalent}{i < cloudMappings.length - 1 ? ', ' : ''}</span>
          ))}
        </Alert>
      )}

      {/* The Ask */}
      {reqs.the_ask && (
        <Paper sx={{ p: 3, border: '1px solid #E2E8F0', borderLeft: '4px solid #6D28D9' }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>📋 The Ask</Typography>
          <Typography variant="body1">{reqs.the_ask}</Typography>
        </Paper>
      )}

      {/* AWS Services */}
      {reqs.aws_services_mentioned && reqs.aws_services_mentioned.length > 0 && (
        <Paper sx={{ p: 3, border: '1px solid #E2E8F0' }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>☁️ AWS Services Identified</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {reqs.aws_services_mentioned.map((svc, i) => (
              <Chip key={i} label={svc} size="small" sx={{ bgcolor: '#F3E8FF', color: '#6D28D9' }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* Deliverables + Requirements */}
      <Paper sx={{ p: 3, border: '1px solid #E2E8F0' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Extracted from RFP <Chip label={analysis.requirement_count} size="small" color="primary" sx={{ ml: 1 }} />
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {[
            ['Deliverables', reqs.deliverables?.length || 0, '#6D28D9'],
            ['Functional', reqs.functional_requirements?.length || 0, '#DB2777'],
            ['Non-Functional', reqs.non_functional_requirements?.length || 0, '#D97706'],
            ['Constraints', reqs.technical_constraints?.length || 0, '#0891B2'],
            ['Integrations', reqs.integrations?.length || 0, '#059669'],
          ].map(([label, count, color]) => (
            <Box key={label} sx={{ bgcolor: `${color}08`, border: `1px solid ${color}25`, borderRadius: 2, p: 2, textAlign: 'center', flex: 1 }}>
              <Typography variant="h4" fontWeight={700} sx={{ color }}>{count}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Summary */}
      <Paper sx={{ p: 3, border: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>AI-Generated Summary</Typography>
          <Button size="small" startIcon={<Refresh />} onClick={handleReAnalyze} variant="outlined" color="warning">
            Re-analyze
          </Button>
        </Box>
        <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 2, p: 3, maxHeight: 350, overflow: 'auto', border: '1px solid #E2E8F0' }}>
          <Markdown>{summary}</Markdown>
        </Box>
      </Paper>

      {/* Edit */}
      <Paper sx={{ p: 3, border: '1px solid #E2E8F0' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Your Review & Edits</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>Edit the summary below. AI will re-summarize incorporating your changes.</Alert>
        <TextField multiline rows={10} fullWidth value={editText} onChange={e => setEditText(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: 13 } }} />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={onBack}>← Back</Button>
          <Button variant="outlined" onClick={handleReSummarize} disabled={reSumLoading}>
            {reSumLoading ? 'Re-summarizing...' : '↻ Re-summarize with my edits'}
          </Button>
          <Button variant="contained" fullWidth onClick={() => { setSession(s => ({ ...s, finalSummary: editText, analysis: { ...analysis, summary } })); onNext(); }}
            sx={{ bgcolor: '#6D28D9', '&:hover': { bgcolor: '#5B21B6' } }}>
            Approve & Continue →
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
